import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowDown } from "lucide-react";
import { PATHWAY_STAGES } from "../data/content";
import { scrollToId } from "../lib/scroll";

const EASE = [0.16, 1, 0.3, 1];

const MaskedLine = ({ children, delay }) => (
  <span className="masked-line">
    <motion.span
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.1, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const glowY = useTransform(scrollYProgress, [0, 1], [0, 180]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section
      id="home"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen flex flex-col justify-center overflow-hidden bg-[#070D1D]"
    >
      <div className="absolute inset-0 hero-grid-lines" />
      <motion.div
        style={{ y: glowY }}
        className="absolute -top-40 left-1/2 -translate-x-1/2 w-[900px] h-[600px] rounded-full bg-teal-500/[0.09] blur-[140px] pointer-events-none"
      />
      <motion.div
        style={{ y: glowY }}
        className="absolute bottom-0 right-[-200px] w-[600px] h-[500px] rounded-full bg-[#0B132B] blur-[100px] pointer-events-none"
      />
      <motion.img
        src="/hummingbird.png"
        alt=""
        aria-hidden="true"
        style={{ y: glowY }}
        className="absolute right-[-90px] top-[14%] w-[560px] max-w-none opacity-[0.07] pointer-events-none select-none"
        data-testid="hero-bird-watermark"
      />

      <motion.div style={{ y: textY, opacity: fade }} className="relative max-w-7xl mx-auto px-6 sm:px-10 w-full pt-36 pb-20">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.2 }}
          className="font-mono text-xs uppercase tracking-[0.35em] text-teal-400 mb-8 flex items-center gap-3"
          data-testid="hero-eyebrow"
        >
          <span className="w-8 h-px bg-teal-500 inline-block" />
          Calliope · Life Sciences Advisory
        </motion.p>

        <h1 className="font-serif text-[11vw] sm:text-6xl lg:text-7xl leading-[1.05] tracking-tight text-[#F8F9FA]" data-testid="hero-headline">
          <MaskedLine delay={0.35}>Connecting Expertise</MaskedLine>
          <MaskedLine delay={0.5}>
            to <em className="italic text-teal-300">Advance Science.</em>
          </MaskedLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.9, ease: EASE }}
          className="mt-8 max-w-xl text-base sm:text-lg text-slate-400 leading-relaxed"
          data-testid="hero-subheadline"
        >
          Strategic advisory for life sciences organizations navigating complex scientific,
          clinical, regulatory and organizational decisions.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1.1, ease: EASE }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-cta-primary"
            onClick={() => scrollToId("contact")}
            className="group inline-flex items-center gap-3 bg-teal-600 hover:bg-teal-500 text-white text-sm font-semibold tracking-wide px-7 py-4 transition-colors duration-300"
          >
            Book a Strategic Conversation
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
          </button>
          <button
            data-testid="hero-cta-secondary"
            onClick={() => scrollToId("pathway")}
            className="inline-flex items-center gap-3 border border-white/20 hover:border-teal-400/60 text-slate-200 hover:text-teal-300 text-sm font-medium tracking-wide px-7 py-4 transition-colors duration-300"
          >
            Explore the Pathway
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1.2, delay: 1.4 }}
          className="mt-20 pt-8 border-t border-white/10"
          data-testid="hero-pathway-ticker"
        >
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2 font-mono text-[11px] sm:text-xs uppercase tracking-[0.22em] text-slate-500">
            {PATHWAY_STAGES.map((s, i) => (
              <span key={s.num} className="flex items-center gap-3">
                <span className="text-teal-500/80">{s.num}</span>
                <span className="hover:text-slate-300 transition-colors duration-300">{s.name}</span>
                {i < PATHWAY_STAGES.length - 1 && <span className="text-slate-700">→</span>}
              </span>
            ))}
          </div>
        </motion.div>
      </motion.div>

      <motion.button
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 1 }}
        onClick={() => scrollToId("about")}
        data-testid="hero-scroll-indicator"
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-slate-500 hover:text-teal-400 transition-colors"
        aria-label="Scroll down"
      >
        <ArrowDown className="w-5 h-5 animate-bounce" />
      </motion.button>
    </section>
  );
};
